'use client';

import { PrismicNextLink } from '@prismicio/next';
import Link from 'next/link';
import { useParams, usePathname } from 'next/navigation';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import type { HeaderDocument } from '../../../prismicio-types';
import { Logo } from '@/components';
import { routes, screenSizes } from '@/constants';
import { MenuContext } from '@/context';
import { useScreenSize, useScrollDirection } from '@/hooks';
import { getPageName } from '@/utils';
import { IconTimer } from '@/assets';
import { LandingPageContent, NavigationMenu } from './parts';
import useModal from '@/hooks/useModal';
import { MODAL_NAMES } from '@/constants/modals';
import { ProspectData } from '@/types/salesforce';
import { SalesforceService } from '@/lib/Services/SalesforceService';

import styles from './header.module.scss';

const SCROLLED_PIXELS_TRIGGER_MOBILE = 40;

type Props = {
  page: HeaderDocument<string>;
};

export const Header: FC<Props> = ({ page }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpenBurgerMenu, setIsOpenBurgerMenu] = useState<boolean>(false);
  const [prospectData, setProspectData] = useState<ProspectData | null>();
  const { id } = useParams();
  const pathname = usePathname();
  const route = getPageName(pathname);
  const { openModal } = useModal();

  const scrollDirection = useScrollDirection();
  const screenSize = useScreenSize();

  const isLandingPage =
    route === routes.landingPage ||
    route === routes.preQualified ||
    route === routes.engineLanding;
  const isEngineLanding = route === routes.engineLanding;

  const isPreQualifiedPage = route === routes.preQualified;

  const isMobileScroll =
    isScrolled &&
    scrollDirection === 'down' &&
    screenSize.width <= screenSizes.desktop;

  const scrolledStyles = isScrolled ? styles.isScrolled : '';
  const headerStyles = `${styles.header} ${isOpenBurgerMenu ? styles.isOpen : ''} ${isLandingPage ? styles.landing_page : ''} ${isEngineLanding ? styles.portal : ''} ${isMobileScroll ? styles.hide : ''}`;

  const handleToggleMenu = () => {
    if (isOpenBurgerMenu) {
      document.body.classList.remove('no-scroll');
    } else {
      document.body.classList.add('no-scroll');
    }
    setIsOpenBurgerMenu((prev) => !prev);
  };

  const handleScroll = useCallback(() => {
    setIsScrolled(
      screenSize.width < screenSizes.desktop &&
        window.scrollY > SCROLLED_PIXELS_TRIGGER_MOBILE
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEngineLanding) {
      (async function getProspectData() {
        try {
          const res = await SalesforceService.getProspectRecord(id as string);
          setProspectData(res.data);
        } catch {
          setProspectData(null);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const menuContext = useMemo(
    () => ({ toggleMenu: handleToggleMenu }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpenBurgerMenu]
  );

  return (
    <MenuContext.Provider value={menuContext}>
      {isLandingPage &&
        (isPreQualifiedPage ? (
          <div
            onClick={() => openModal({ name: MODAL_NAMES.preQualified })}
            className={styles.getInstantWrapper}
          >
            <IconTimer />
            <p className={styles.getInstantText}>
              Get instant offers in under 60 seconds
            </p>
          </div>
        ) : (
          !isEngineLanding && (
            <PrismicNextLink
              field={page.data.button_link}
              className={`${styles.getInstantWrapper} ${scrolledStyles}`}
            >
              <IconTimer />
              <p className={styles.getInstantText}>
                Get instant offers in under 60 seconds
              </p>
            </PrismicNextLink>
          )
        ))}
      <header
        className={`${headerStyles} ${isLandingPage ? scrolledStyles : ''}`}
      >
        <Link href="/">
          <Logo logo={page.data.logo_dark} />
        </Link>

        {!isLandingPage && (
          <NavigationMenu
            page={page}
            isOpen={isOpenBurgerMenu}
            onClick={handleToggleMenu}
          />
        )}

        {isLandingPage && (
          <LandingPageContent
            page={page}
            isPreQualifiedPage={isPreQualifiedPage}
            isEngineLanding={isEngineLanding}
            prospect={prospectData}
          />
        )}
      </header>
      {!isLandingPage && (
        <>
          {isOpenBurgerMenu && (
            <div
              onClick={handleToggleMenu}
              className={styles.shadow_background}
            />
          )}
        </>
      )}
    </MenuContext.Provider>
  );
};
